import { useState, useEffect } from 'react'
import { Table, Button, Space, Tag, Popconfirm, Input, List, Grid } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import CustomerFormModal from '@/components/CustomerFormModal'
import { useCustomers, useDeleteCustomer } from '@/hooks/useCustomers'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { usePermission } from '@/hooks/usePermission'
import type { Customer } from '@/types'

const { useBreakpoint } = Grid

const CustomersPage = () => {
  const { setTitle } = usePageTitle()
  const canCreate = usePermission('customers.create')
  const canEdit   = usePermission('customers.edit')
  const canDelete = usePermission('customers.delete')
  const [searchText, setSearchText] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const deleteMutation = useDeleteCustomer()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  useEffect(() => {
    setTitle('Gestione Clienti')
  }, [setTitle])

  // Carica tutti i clienti per permettere ordinamento completo
  const { data, isLoading, refetch } = useCustomers(1, 1000, searchText)

  const handleCreate = () => {
    setEditingCustomer(null)
    setIsModalOpen(true)
  }

  const handleEdit = (record: Customer) => {
    setEditingCustomer(record)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id)
  }

  const getCustomerName = (record: Customer) => {
    if (record.tipo === 'azienda' && record.ragione_sociale) return record.ragione_sociale
    return `${record.nome || ''} ${record.cognome || ''}`.trim()
  }

  const columns: ColumnsType<Customer> = [
    {
      title: 'Cliente',
      key: 'name',
      render: (_, record) => {
        if (record.tipo === 'azienda' && record.ragione_sociale) {
          return (
            <Space direction="vertical" size={0}>
              <strong>{record.ragione_sociale}</strong>
              <Tag color="blue" style={{ fontSize: '10px' }}>Azienda</Tag>
            </Space>
          )
        }
        return (
          <Space direction="vertical" size={0}>
            <span>{`${record.nome || ''} ${record.cognome || ''}`.trim()}</span>
            <Tag color="green" style={{ fontSize: '10px' }}>Privato</Tag>
          </Space>
        )
      },
      sorter: (a, b) => {
        const nameA = a.tipo === 'azienda' ? (a.ragione_sociale || '') : `${a.nome || ''} ${a.cognome || ''}`.trim().toLowerCase()
        const nameB = b.tipo === 'azienda' ? (b.ragione_sociale || '') : `${b.nome || ''} ${b.cognome || ''}`.trim().toLowerCase()
        return nameA.localeCompare(nameB, 'it')
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email ? <><MailOutlined /> {email}</> : '-',
    },
    {
      title: 'Telefono',
      key: 'phone',
      render: (_, record) => {
        const phone = record.cellulare || record.telefono
        return phone ? <><PhoneOutlined /> {phone}</> : '-'
      },
    },
    {
      title: 'Codice Fiscale',
      dataIndex: 'codice_fiscale',
      key: 'codice_fiscale',
      render: (code) => code || '-',
    },
    {
      title: 'P.IVA',
      dataIndex: 'partita_iva',
      key: 'partita_iva',
      render: (vat) => vat || '-',
    },
    ...(canEdit || canDelete ? [{
      title: 'Azioni',
      key: 'actions',
      render: (_: any, record: Customer) => (
        <Space>
          {canEdit && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Modifica
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="Sei sicuro di voler eliminare questo cliente?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sì"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Elimina
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ]

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="Cerca cliente..."
          onSearch={setSearchText}
          style={{ flex: '1 1 200px', maxWidth: 300 }}
          allowClear
        />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Nuovo Cliente
          </Button>
        )}
      </div>

      {isMobile ? (
        <List
          loading={isLoading}
          dataSource={data?.items}
          rowKey="id"
          renderItem={(record) => {
            const phone = record.cellulare || record.telefono
            const isAzienda = record.tipo === 'azienda' && record.ragione_sociale
            return (
              <List.Item
                style={{ padding: '10px 0' }}
                actions={[
                  ...(canEdit ? [<Button
                    key="edit"
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />] : []),
                  ...(canDelete ? [<Popconfirm
                    key="delete"
                    title="Eliminare questo cliente?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Sì"
                    cancelText="No"
                  >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>] : []),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space size={6}>
                      <strong>{getCustomerName(record)}</strong>
                      <Tag color={isAzienda ? 'blue' : 'green'} style={{ fontSize: '10px' }}>
                        {isAzienda ? 'Azienda' : 'Privato'}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={2}>
                      {record.email && <span><MailOutlined /> {record.email}</span>}
                      {phone && <span><PhoneOutlined /> {phone}</span>}
                    </Space>
                  }
                />
              </List.Item>
            )
          }}
          pagination={{
            pageSize: 20,
            showTotal: (total) => `Totale ${total} clienti`,
            size: 'small',
          }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={data?.items}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize,
            onShowSizeChange: (_, size) => setPageSize(size),
            showTotal: (total) => `Totale ${total} clienti`,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      )}

      <CustomerFormModal
        open={isModalOpen}
        editingCustomer={editingCustomer}
        onCancel={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false)
          setEditingCustomer(null)
          refetch()
        }}
      />
    </div>
  )
}

export default CustomersPage
